/**
 * V2 write-side helpers for the in-store `ThemeManager`. Pairs with:
 *   - `GET  /brandapp-v2/themes/:appId/scopes/:scope/versions/:version`
 *   - `PUT  /brandapp-v2/themes/:appId/scopes/:scope/versions/:version`
 *   - `POST /brandapp-v2/themes/:appId/scopes/:scope/asset-upload-url`
 *
 * Identity lives on the URL the portal opens (operator hands the store a
 * short-lived `editThemeToken` plus `appId` + `scope` + `version`), so the
 * ThemeManager itself never needs a brand JWT. `getV2EditTarget` returns
 * `null` when the URL doesn't carry a full V2 target — callers then fall
 * through to the existing V1 write paths.
 *
 * All endpoints authenticate via the `x-edit-theme-token` header; the
 * server validates it against `(appId, scope, version)` bound at mint time.
 */

import { API_BASE } from "../utils/domain";
import { getThemeParams } from "../utils/themeStorage";

export interface V2EditTarget {
  appId: string;
  scope: string;
  version: string;
  editThemeToken: string;
}

export interface V2ThemeVersionResponse<TData = Record<string, any>> {
  scope: string;
  version: string;
  data: TData;
}

export interface V2AssetUploadResponse {
  signedUploadUrl: string;
  objectPublicUrl: string;
}

/**
 * Read the V2 edit target from the current page URL, falling back to
 * localStorage (ThemeManager syncs URL→storage on mount). Returns `null`
 * if any of `appId` / `scope` / `version` / `editThemeToken` is missing —
 * V1 mode.
 *
 * Storage fallback matters because in-app client routing and ThemeManager
 * iframe URL sync can drop `version` from the address bar while the
 * operator is still editing that version.
 */
export const getV2EditTarget = (): V2EditTarget | null => {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const stored = getThemeParams();
    const appId = params.get("appId") || stored.appId;
    const scope = params.get("scope") || stored.scope;
    const version = params.get("version") || stored.version;
    const editThemeToken = params.get("editThemeToken") || stored.editThemeToken;
    if (!appId || !scope || !version || !editThemeToken) return null;
    return { appId, scope, version, editThemeToken };
  } catch {
    return null;
  }
};

const buildVersionUrl = (target: V2EditTarget): string =>
  `${API_BASE}/brandapp-v2/themes/${encodeURIComponent(target.appId)}/scopes/${encodeURIComponent(target.scope)}/versions/${encodeURIComponent(target.version)}`;

export interface V2GetVersionOptions {
  /**
   * When true (widget ThemeManager display loads), server merges Concierge
   * Studio overrides into `data` and attaches `studioControlledKeys` etc.
   * Leave false for read-modify-write patches so studio values are never
   * written into the version row.
   */
  includeStudioOverlay?: boolean;
}

export type V2ThemeVersionWithStudio = V2ThemeVersionResponse & {
  studioControlledKeys?: unknown;
  studioAvailability?: unknown;
  studioAgentName?: string;
  studioAgreement?: unknown;
  studioSupportSkills?: unknown;
  studioSupportBranding?: unknown;
  studioVoiceSkills?: unknown;
  studioVoiceBranding?: unknown;
};

/**
 * Fetch the version row being edited. Throws if the version is missing.
 */
export const v2GetVersion = async (
  target: V2EditTarget,
  options?: V2GetVersionOptions,
): Promise<V2ThemeVersionWithStudio> => {
  const url = options?.includeStudioOverlay
    ? `${buildVersionUrl(target)}?includeStudioOverlay=true`
    : buildVersionUrl(target);
  const response = await fetch(url, {
    headers: { "x-edit-theme-token": target.editThemeToken },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch V2 theme version: ${response.status} ${response.statusText}`);
  }
  const body = await response.json();
  const {
    data,
    studioControlledKeys,
    studioAvailability,
    studioAgentName,
    studioAgreement,
    studioSupportSkills,
    studioSupportBranding,
    studioVoiceSkills,
    studioVoiceBranding,
  } = body ?? {};
  return {
    scope: target.scope,
    version: target.version,
    data: (data ?? {}) as Record<string, any>,
    studioControlledKeys,
    studioAvailability,
    studioAgentName,
    studioAgreement,
    studioSupportSkills,
    studioSupportBranding,
    studioVoiceSkills,
    studioVoiceBranding,
  };
};

/** @deprecated Use {@link v2GetVersion}. Kept as an alias for call-site churn. */
export const v2GetDraft = v2GetVersion;

/**
 * Overwrite the version with the supplied blob. Returns the server-echoed row.
 */
export const v2SaveVersion = async (
  target: V2EditTarget,
  themeData: Record<string, any>,
): Promise<unknown> => {
  const response = await fetch(buildVersionUrl(target), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "x-edit-theme-token": target.editThemeToken,
    },
    body: JSON.stringify({ themeData }),
  });
  if (!response.ok) {
    throw new Error(`Failed to save V2 theme version: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/** @deprecated Use {@link v2SaveVersion}. */
export const v2SaveDraft = v2SaveVersion;

/**
 * Mint an S3 signed PUT URL for a theme asset. The client is expected to
 * `PUT` bytes directly to `signedUploadUrl`, then reference the returned
 * `objectPublicUrl` in the theme blob that it saves via `v2SaveVersion`.
 */
export const v2GenerateAssetUploadUrl = async (
  target: V2EditTarget,
  input: { fileName: string; contentType: string; assetType: "font" | "icon" | "image" },
): Promise<V2AssetUploadResponse> => {
  const url =
    `${API_BASE}/brandapp-v2/themes/${encodeURIComponent(target.appId)}/scopes/${encodeURIComponent(target.scope)}/asset-upload-url` +
    `?version=${encodeURIComponent(target.version)}`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-edit-theme-token": target.editThemeToken,
    },
    body: JSON.stringify({ ...input, version: target.version }),
  });
  if (!response.ok) {
    throw new Error(`Failed to generate V2 asset upload URL: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Shared write path used by every granular mutator. `patcher` receives a
 * MUTABLE copy of the version `data` and should apply the mutation in-place.
 */
export const v2PatchVersion = async (
  target: V2EditTarget,
  patcher: (data: Record<string, any>) => void,
): Promise<Record<string, any>> => {
  const { data } = await v2GetVersion(target);
  const patched: Record<string, any> = { ...data };
  patched.colors = { ...(data.colors ?? {}) };
  patched.fonts = { ...(data.fonts ?? {}) };
  patched.icons = { ...(data.icons ?? {}) };
  patched.widgetConfig = { ...(data.widgetConfig ?? {}) };
  patcher(patched);
  await v2SaveVersion(target, patched);
  return patched;
};

/** @deprecated Use {@link v2PatchVersion}. */
export const v2PatchDraft = v2PatchVersion;
